// src/components/dashboard/EmissionsReductionSummary.js
import React, { useMemo } from "react";
import { getSectorColor } from "../../config/colorScheme";
import { useSectorEmissionsWithFood } from "../../hooks/useSectorEmissionsWithFood";

// clamp helper
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
function pctReduction(fromVal, toVal) {
  if (typeof fromVal !== "number" || !isFinite(fromVal) || fromVal === 0) return 0;
  return ((fromVal - toVal) / fromVal) * 100;
}

export default function EmissionsReductionSummary({
  scenario,          // backend scenario key
  from = 2050,       // optional: restrict query/labels
  to = 2050,
  compareYear = 2025 // baseline year
}) {
  // IMPORTANT: pass only EMIS sectors here; FOOD is fetched by the hook's second field
  const { labels, byGroupYear, loading, error } =
    useSectorEmissionsWithFood({ scenario, from, to, emisSectors: ["TRA", "RES", "IND", "FOOD"] });

  // normalise label types to numbers for indexing
  // normalise label types to numbers for indexing
    const yearLabels = useMemo(
      () => (labels ?? [])
        .map((y) => (typeof y === "number" ? y : parseInt(y, 10)))
        .filter(Number.isFinite),
      [labels]
    );

    // Build arrays aligned to yearLabels for each of the four sectors
    const seriesBySector = useMemo(() => {
      const get = (code, year) => (byGroupYear?.[code]?.[year] ?? 0);
      return {
        food:       yearLabels.map((y) => get("FOOD", y)),
        buildings:  yearLabels.map((y) => get("BUILDINGS", y)),
        transport:  yearLabels.map((y) => get("TRA", y)),
        industry:   yearLabels.map((y) => get("IND", y)),
      };
    }, [byGroupYear, yearLabels]);

    const {
      totalPct,
      sectorPercents,   // { food, buildings, transport, industry }
      baselineYear,
      targetYear
    } = useMemo(() => {
      if (!yearLabels.length) {
        return { totalPct: 0, sectorPercents: {}, baselineYear: compareYear, targetYear: to };
      }

      const baseIdx = yearLabels.includes(compareYear)
        ? yearLabels.indexOf(compareYear)
        : 0;

      const tgtIdx = yearLabels.includes(to) ? yearLabels.indexOf(to) : yearLabels.length - 1;

      const sectors = ["food", "buildings", "transport", "industry"];

      // sector-level % reduction
      const sectorPercents = sectors.reduce((acc, key) => {
        const series = seriesBySector[key] || [];
        const base = series[baseIdx] ?? null;
        const tgt = series[tgtIdx] ?? null;
        const pct = pctReduction(base, tgt);
        acc[key] = isFinite(pct) ? pct : 0;
        return acc;
      }, {});

      // overall % = sum(baseline) vs sum(target)
      const baseTotal = sectors.reduce((sum, k) => {
        const v = seriesBySector[k]?.[baseIdx];
        return sum + (typeof v === "number" ? v : 0);
      }, 0);

      const tgtTotal = sectors.reduce((sum, k) => {
        const v = seriesBySector[k]?.[tgtIdx];
        return sum + (typeof v === "number" ? v : 0);
      }, 0);

      const totalPct = pctReduction(baseTotal, tgtTotal);

    return {
      totalPct: isFinite(totalPct) ? totalPct : 0,
      sectorPercents,
      baselineYear: yearLabels[baseIdx],
      targetYear: yearLabels[tgtIdx],
    };
  }, [yearLabels, seriesBySector, compareYear, to]);

  if (loading) return <div className="w-full h-40 grid place-items-center">Calculating reductions…</div>;
  if (error) return <div className="w-full h-40 text-red-600">Error: {error.message}</div>;

  // nice render helpers
  const pretty = (n) => `${(Math.round(n * 10) / 10).toFixed(1)}%`;
  const sectorsOrder = ["food", "buildings", "transport", "industry"];
  const labelMap = { food: "Food", buildings: "Buildings", transport: "Transport", industry: "Industry" };

  return (
    <div className="text-center">
        <p className="text-xs sm:text-sm">

                <strong className="text-orange-800">
                    Your impact: The choices you've made could reduce UK emissions by   {pretty(totalPct)}  by 2050 compared to current emissions
                </strong>
      {/* if adopted at your selected population levels. */}
              </p>

    </div>
  );
}
